<template>
  <div class="profil-risque">
    <div v-if="showClientUI" class="client-page">
      <div v-if="loadingProfile" class="profil-risque__state" role="status" aria-live="polite">
        <span class="profil-risque__spinner" aria-hidden="true" />
        <p class="profil-risque__state-text">Chargement de votre profil de risque…</p>
      </div>

      <div v-else-if="existingProfile && !showForm" class="client-card client-status">
        <div class="client-gauge" :style="{ '--client-gauge-value': existingScorePct }">
          <span class="client-gauge__value">{{ existingScorePct }}%</span>
        </div>
        <div class="client-status__body">
          <h3 class="client-status__title">Profil {{ existingProfile.categorie }}</h3>
          <p v-for="item in existingCategoryDescription" :key="item.id" class="client-status__text">
            {{ item.text }}
          </p>
          <p class="profil-risque__meta">
            Enregistré le {{ formattedDate }}
          </p>
          <button type="button" class="client-btn client-btn--ghost" @click="showForm = true">
            Refaire le questionnaire
          </button>
        </div>
      </div>

      <template v-else>
        <p v-if="error" class="client-alert client-alert--error">{{ error }}</p>
        <ProfilRisqueQuestionnaire :submitting="submitting" @submit="onSubmit" />
      </template>
    </div>

    <div v-else-if="!roleChecked" class="profil-risque__state" role="status" aria-live="polite">
      <span class="profil-risque__spinner" aria-hidden="true" />
      <p class="profil-risque__state-text">Ouverture de votre profil de risque…</p>
    </div>

    <div v-else class="client-page">
      <div class="client-card client-empty">
        <span class="client-empty__icon" aria-hidden="true">
          <LockClosedIcon />
        </span>
        <h1 class="client-empty__title">Profil de risque réservé aux clients</h1>
        <p class="client-empty__text">
          Ce questionnaire n’est accessible qu’avec un compte client. Votre compte donne accès aux
          espaces de suivi et de validation des dossiers.
        </p>
        <router-link to="/analyse-patrimoniale/dashboards" class="client-btn client-btn--primary">
          Retour au tableau de bord
        </router-link>
      </div>
    </div>

    <ProfilRisqueResultModal
      :visible="showResultModal"
      :category="existingCategoryEntry"
      :score-pct="existingScorePct"
      :horizon-label="existingHorizonLabel"
      @close="onResultModalClose"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { LockClosedIcon } from '@heroicons/vue/24/outline';
import ProfilRisqueQuestionnaire from '@/components/patrimoine/ProfilRisqueQuestionnaire.vue';
import ProfilRisqueResultModal from '@/components/patrimoine/ProfilRisqueResultModal.vue';
import { fetchRiskProfile, saveRiskProfile } from '@/api/riskProfile';
import { CATEGORIES } from '@/utils/profilInvest';
import { getUserRole, isClientFacingRole } from '@/utils/auth';
import { syncEmployeProfileFromApi } from '@/utils/permissions';

const HORIZON_LABELS = {
  '1_3y': 'Court terme (moins de 3 ans)',
  '3_7y': 'Moyen terme (3 à 7 ans)',
  '7_20y': 'Long terme (7 ans et plus)',
};

/**
 * Le rôle est relu après synchronisation serveur : sans cela, un client dont la
 * session vient d'être restaurée voyait le message « réservé aux clients »
 * (même logique que MonAnalyse.vue).
 */
const userRole = ref(getUserRole() || '');
const roleChecked = ref(false);
const loadingProfile = ref(true);
const submitting = ref(false);
const error = ref('');
const existingProfile = ref(null);
/** Reste sur le résumé tant que l'utilisateur ne demande pas explicitement à refaire le test. */
const showForm = ref(false);
/** Popup festive, affichée uniquement juste après une soumission réussie. */
const showResultModal = ref(false);

const showClientUI = computed(() => isClientFacingRole(userRole.value));

const existingCategoryEntry = computed(() =>
  CATEGORIES.find((c) => c.label === existingProfile.value?.categorie) || null
);
const existingCategoryDescription = computed(() => existingCategoryEntry.value?.description || []);

const existingScorePct = computed(() => {
  if (!existingProfile.value) return 0;
  const min = CATEGORIES[0].min;
  const max = CATEGORIES[CATEGORIES.length - 1].max;
  const pct = Math.round(((existingProfile.value.score - min) / (max - min)) * 100);
  return Math.min(100, Math.max(0, pct));
});

const existingHorizonLabel = computed(() => HORIZON_LABELS[existingProfile.value?.horizon_band] || '');

const formattedDate = computed(() => {
  const raw = existingProfile.value?.date_modification;
  if (!raw) return '';
  try {
    return new Date(raw).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
});

onMounted(async () => {
  await syncEmployeProfileFromApi();
  userRole.value = getUserRole() || '';
  roleChecked.value = true;

  if (!isClientFacingRole(userRole.value)) return;
  try {
    existingProfile.value = await fetchRiskProfile();
    showForm.value = !existingProfile.value;
  } catch (e) {
    // Pas de profil existant récupérable : on part du formulaire vierge.
    showForm.value = true;
  } finally {
    loadingProfile.value = false;
  }
});

async function onSubmit(payload) {
  submitting.value = true;
  error.value = '';
  try {
    // Traduit la forme émise par ProfilRisqueQuestionnaire.vue vers les champs
    // attendus par patrimoine.internal_api.save_risk_profile. riskPreference n'est
    // pas envoyé : c'est une heuristique dérivée côté frontend (catégorie -> engine
    // wealth_coach), pas une donnée du questionnaire — non stockée pour l'instant.
    existingProfile.value = await saveRiskProfile({
      reponses: payload.answers,
      reponses_matrice: payload.matrixAnswers,
      score: payload.score,
      categorie: payload.category,
      horizon_band: payload.horizonBand,
    });
    // Le formulaire reste affiché sous la popup (fond flouté) ; on ne bascule
    // sur le résumé persistant qu'à la fermeture de la popup, cf. onResultModalClose.
    showResultModal.value = true;
  } catch (e) {
    error.value =
      e?.response?.data?.detail || 'Impossible d’enregistrer votre profil de risque pour le moment.';
  } finally {
    submitting.value = false;
  }
}

function onResultModalClose() {
  showResultModal.value = false;
  if (existingProfile.value) showForm.value = false;
}
</script>

<style scoped>
.profil-risque__state {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-6);
}

.profil-risque__state-text {
  font-family: var(--font-body);
  color: var(--client-text-muted, #6b6155);
  margin: 0;
}

.profil-risque__spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--client-line, rgba(84, 57, 41, 0.1));
  border-top-color: var(--brand-gold);
  animation: profil-risque-spin 0.7s linear infinite;
}

@keyframes profil-risque-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .profil-risque__spinner {
    animation: none;
  }
}

.profil-risque__meta {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--client-text-muted, #64748b);
  margin: var(--space-2) 0 var(--space-4);
}
</style>
